import { AxiosResponse } from 'axios'
import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { HISTORY_FIELDS } from '../systemDataHandler'
import { HistoryData, SystemInfo, TemperatureData } from '../models/systemDataStoreModel'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import { PowermeterHistory } from '../../../Types/responses/powermeter-values/{sn}'

export const fetchHistoryData = async (
  systemInfo: SystemInfo,
  from: number,
  to: number,
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>,
  signal?: AbortSignal
): Promise<HistoryData[]> => {
  const serialNo = systemInfo.serialNo

  const res = await authFetch({
    path: 'storages/' + serialNo + '/history-values',
    method: 'GET',
    payload: {
      sort: '+time',
      filter: `time>=${from},time<=${to}`,
      fields: HISTORY_FIELDS.join(','),
    },
    abortSignal: signal,
  })

  return objectApiResult(res.data as any) as HistoryData[] //eslint-disable-line @typescript-eslint/no-explicit-any
}

export const fetchTemperatureData = (
  systemInfo: SystemInfo,
  from: number,
  to: number,
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>,
  signal?: AbortSignal
): Promise<TemperatureData[]> => {
  const serialNo = systemInfo.serialNo
  return authFetch({
    path: `weather-forecast/forecast/${serialNo}/temp`,
    method: 'GET',
    endpoint: process.env.REACT_APP_WEATHER_API_ENDPOINT,
    payload: {
      time_min: from,
      time_max: to,
    },
    abortSignal: signal,
  })
    .then(r => objectApiResult(r.data as any)) //eslint-disable-line @typescript-eslint/no-explicit-any
    .catch(e => {
      throw e
    }) as Promise<TemperatureData[]>
}

export const fetchWallboxHistoryData = (
  systemInfo: SystemInfo,
  from: number,
  to: number,
  index: number,
  id: number,
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>,
  signal?: AbortSignal
): Promise<{ id: number; data: WallboxHistory[] }> => {
  const serialNo = systemInfo.serialNo

  return authFetch({
    path: `wallboxes/${serialNo}/${index}/historydata`,
    payload: {
      sort: '+time',
      filter: `time>=${from},time<=${to}`,
    },
    method: 'GET',
    abortSignal: signal,
  })
    .then(r => ({ id: index, data: objectApiResult(r.data as any) })) //eslint-disable-line @typescript-eslint/no-explicit-any
    .catch(e => {
      throw e
    }) as Promise<{ id: number; data: WallboxHistory[] }>
}

export const fetchPowermeterHistoryData = (
  systemInfo: SystemInfo,
  from: number,
  to: number,
  id: number,
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>,
  storageId?: string,
  signal?: AbortSignal
): Promise<{ id: number; uniqueId: string; data: PowermeterHistory[] }> => {
  const storageIdToUse = storageId || systemInfo.serialNo
  const uniqueId = storageIdToUse ? `_${storageIdToUse}_${id}` : `_${id}`
  return authFetch({
    path: `storages/${storageIdToUse}/powermeter-values/${id}`,
    payload: {
      sort: '+time',
      filter: `time>=${from},time<=${to}`,
      fields: 'time,NetIn,NetOut',
    },
    method: 'GET',
    abortSignal: signal,
  })
    .then(r => ({ id, uniqueId, data: objectApiResult(r.data as any) })) //eslint-disable-line @typescript-eslint/no-explicit-any
    .catch(e => {
      throw e
    }) as Promise<{ id: number; uniqueId: string; data: PowermeterHistory[] }>
}
